import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGroupedActionsQuery,
  useCampaignGroupedActionsQuery,
  useUndoAction
} from '@/lib/api-extensions/action-queries';
import { PaginationParams, NormalizedAction, ActionLogBase } from '@/types/action-types';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/useToast';
import { Spinner } from '@/components/ui/spinner';
import { SimpleTaskItem } from './SimpleTaskItem';
import { SimpleCampaignTaskItem } from './SimpleCampaignTaskItem';
import { EmptyStateView } from './EmptyStateView';
import { normalizeAction } from './normalize-action';
import { KeywordList } from './KeywordList';
import { BudgetDetail } from './BudgetDetail';
import { GenericDetail } from './GenericDetail';
import { TaskDetailModal } from './TaskDetailModal';
import { PageHeader } from '@/components/campaign/page-header';
import { CampaignGroup } from '@/lib/utils/task-inbox-grouping';

interface TwoColumnTasksInboxViewProps {
  campaignId?: number; // Make campaignId optional to support both campaign-specific and all tasks views
}

export function TwoColumnTasksInboxView({ campaignId }: TwoColumnTasksInboxViewProps = {}) {
  const { t } = useTranslation('tasks');
  const { toast } = useToast();

  // State for pagination, selection
  // Unused filter state variable has been removed
  const [pagination, setPagination] = useState<PaginationParams>({ skip: 0, limit: 1000 });
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'reverted'>('active');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  
  // New state for tracking selected action and keyword
  const [selectedAction, setSelectedAction] = useState<NormalizedAction | null>(null);
  const [selectedKeywordId, setSelectedKeywordId] = useState<number | undefined>();

  // Create filters based on active tab
  const filters = activeTab === 'all' ? {} : { is_undone: activeTab === 'reverted' };

  // Tasks data query using the new grouped actions endpoint
  const { data, isLoading, isError, error, refetch } = campaignId
    ? useCampaignGroupedActionsQuery({ campaignId })
    : useGroupedActionsQuery({
        filters,
        pagination
      });

  // Undo action mutation
  const undoActionMutation = useUndoAction();

  // Initialize query parameters based on activeTab on first render
  // Removed setFilters call as it's not defined

  // Use campaign groups directly from the backend response
  const campaignGroups = useMemo(() => {
    if (!data?.items) return [];
    return data.items;
  }, [data]);

  // Get the selected campaign group
  const selectedGroup = useMemo(() => {
    if (!selectedItem) return null;
    return campaignGroups.find((group) => group.id === selectedItem) || null;
  }, [selectedItem, campaignGroups]);
  
  // Create an enhanced action with keywords from all tasks in the group
  const enhancedAction = useMemo(() => {
    if (!selectedGroup || !selectedGroup.tasks || selectedGroup.tasks.length === 0) {
      return null;
    }
    
    
    // Start with the normalized version of the first task
    const baseAction = normalizeAction(selectedGroup.tasks[0]);
    
    
    // If baseAction is null or this isn't a keyword action, just return the base action
    const keywordActionTypes = ['ADD_KEYWORDS', 'ADD_EXACT_MATCH_KEYWORD', 'PAUSE_KEYWORDS', 'DELETE_KEYWORDS', 'ADD_NEGATIVE_KEYWORD', 'ADD_NEGATIVE_KEYWORD_FOR_AD', 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN'];
    if (!baseAction || !keywordActionTypes.includes(baseAction.actionType)) {
      return baseAction;
    }
    
    // Create a map to deduplicate keywords by ad ID and text
    const keywordMap = new Map();
    
    // FIXED: Use individual task entity_ids instead of generating fake IDs
    // First, build a map of keyword text to real entity_id from individual tasks
    const keywordTextToEntityId = new Map();
    selectedGroup.tasks.forEach(task => {
      if (task.entity_type === 'keyword') {
        const taskKeywordText = 
          task.after_state?.text || 
          task.after_state?.keyword_text ||
          task.before_state?.text ||
          task.before_state?.keyword_text;
        
        if (taskKeywordText && task.entity_id) {
          keywordTextToEntityId.set(taskKeywordText, task.entity_id);
        }
      }
    });

    // Process ad_actions directly from the group if available
    if (selectedGroup.adActions && selectedGroup.adActions.length > 0) {
      // Create keywords from the adActions data
      selectedGroup.adActions.forEach(adAction => {
        const adId = adAction.entity_id;
        const adName = adAction.ad_name || `Ad #${adId}`;
        // Make sure we get the ad_url properly
        const adUrl = adAction.ad_url || '';
        const adSlug = adAction.ad_slug || '';
        const internalUrl = adAction.internal_url || '';
        
        if (adAction.keywords && Array.isArray(adAction.keywords)) {
          adAction.keywords.forEach((kw, index) => {
            const kwText = kw.text;
            const key = `${adId}-${kwText}`;

            if (!keywordMap.has(key)) {
              const realKeywordId = keywordTextToEntityId.get(kwText);

              keywordMap.set(key, {
                id: realKeywordId || Number(`${adId}${index}`),
                text: kwText,
                matchType: kw.match_type || 'BROAD',
                adId: adId,
                adName: adName,
                adUrl: adUrl,
                adSlug: adSlug,
                internalUrl: internalUrl,
                status: baseAction && baseAction.actionType === 'PAUSE_KEYWORDS' ? 'PAUSED' :
                        baseAction && baseAction.actionType === 'DELETE_KEYWORDS' ? 'REMOVED' : 'ACTIVE',
              });

            }
          });
        }
      });
    }
    
    // If we couldn't get keywords from adActions, fall back to processing task keywords
    if (keywordMap.size === 0) {
      // Process keywords from all tasks in the group
      for (let i = 0; i < selectedGroup.tasks.length; i++) {
        const taskAction = normalizeAction(selectedGroup.tasks[i]);
        if (!taskAction || !taskAction.keywords || taskAction.keywords.length === 0) continue;
        
        taskAction.keywords?.forEach(keyword => {
          const adId = keyword.adId?.toString() || 'default';
          const key = `${adId}-${keyword.text}`;
          
          // Only add if not already in the map
          if (!keywordMap.has(key)) {
            keywordMap.set(key, keyword);
          }
        });
      }
    }
    
    // Get all unique keywords
    const allKeywords = Array.from(keywordMap.values());
    
    
    // Return a merged action with all keywords
    return {
      ...baseAction,
      keywords: allKeywords
    };
  }, [selectedGroup]);

  // Tab change handler
  const handleTabChange = (value: string) => {
    const newTab = value as 'all' | 'active' | 'reverted';
    setActiveTab(newTab);

    // No need to store filters since we directly pass them to the query in the refetch

    // Reset to first page when tab changes
    const newPagination = { ...pagination, skip: 0 };
    setPagination(newPagination);

    // Clear selection
    setSelectedItem(null);
    setSelectedKeywordId(undefined);
  };

  // Handle selection of an item
  const handleSelectItem = (itemId: string) => {
    const newSelectedId = itemId === selectedItem ? null : itemId;
    setSelectedItem(newSelectedId);
    setSelectedKeywordId(undefined); // Reset keyword selection when changing group
  };
  
  // Handle keyword selection
  const handleSelectKeyword = (keywordId: number) => {
    setSelectedKeywordId(keywordId);
  };

  // Handle view details action - find the correct task for the specific keyword
  const handleViewDetails = (action: ActionLogBase, keywordId?: number) => {

    // If no keyword ID provided, just use the provided action
    if (!keywordId || !selectedGroup) {
      setSelectedAction(normalizeAction(action));
      setSelectedKeywordId(keywordId);
      setDetailsModalOpen(true);
      return;
    }
    
    // Find which task contains this specific keyword
    const keyword = enhancedAction?.keywords?.find(k => k.id === keywordId);
    
    if (keyword && selectedGroup.tasks.length > 0) {

      // NEW FIX: Look for individual keyword actions that match this specific keyword
      // Check if this keyword has a corresponding individual action in the task list
      for (const task of selectedGroup.tasks) {

        // For individual keyword actions, check if this task corresponds to our keyword
        if (task.entity_type === 'keyword') {
          // Extract keyword info from task's after_state or before_state
          const taskKeywordText = 
            task.after_state?.text || 
            task.after_state?.keyword_text ||
            task.before_state?.text ||
            task.before_state?.keyword_text;
          
          const taskKeywordAdId = 
            task.after_state?.ad_id ||
            task.before_state?.ad_id ||
            task.entity_id; // entity_id might be the ad_id for keyword actions
          

          // FIXED: Check if this individual task matches our keyword using entity_id
          const keywordMatchesEntityId = keyword.id === task.entity_id;
          const keywordMatchesText = taskKeywordText === keyword.text;
          
          
          if (keywordMatchesEntityId || (keywordMatchesText && taskKeywordAdId === keyword.adId)) {

            // Use the individual task action instead of the bulk action
            const normalizedTask = normalizeAction(task);
            if (normalizedTask) {
              const specificAction = {
                ...normalizedTask,
                keywords: [keyword] // Include the enhanced keyword data
              };
              
              setSelectedAction(specificAction as NormalizedAction);
              setSelectedKeywordId(keywordId);
              setDetailsModalOpen(true);
              return;
            }
          }
        }
      }

      
      // FALLBACK: Original logic for bulk actions or when individual actions aren't found
      for (const task of selectedGroup.tasks) {
        const normalizedTask = normalizeAction(task);
        if (!normalizedTask || !normalizedTask.keywords) continue;
        
        
        const containsKeyword = normalizedTask.keywords?.some(k => 
          k.id === keywordId || 
          (k.text === keyword.text && k.adId === keyword.adId)
        );
        
        if (containsKeyword) {

          // Create a modified action with just this keyword
          const specificAction = {
            ...normalizedTask,
            keywords: [keyword]
          };
          
          setSelectedAction(specificAction as NormalizedAction);
          setSelectedKeywordId(keywordId);
          setDetailsModalOpen(true);
          return;
        }
      }
    }
    
    
    // Fallback: Create an action containing only the selected keyword
    if (keyword) {
      const baseAction = normalizeAction(selectedGroup.tasks[0]);

      const specificAction = {
        ...baseAction,
        keywords: [keyword]
      };
      
      setSelectedAction(specificAction as NormalizedAction);
      setSelectedKeywordId(keywordId);
      setDetailsModalOpen(true);
    } else {
      // Last resort fallback
      setSelectedAction(normalizeAction(action));
      setSelectedKeywordId(keywordId);
      setDetailsModalOpen(true);
    }
  };

  // Refetch data and handle UI updates
  const queryClient = useQueryClient();

  const refreshData = (): void => {
    // Reset selected item first to ensure clean UI state
    setSelectedItem(null);
    setSelectedKeywordId(undefined);

    // First invalidate any cached data for action-related queries
    if (campaignId) {
      // For campaign-specific views
      queryClient.invalidateQueries({ queryKey: ['grouped-actions', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-overview-actions', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['actionLogs', campaignId] });
    } else {
      // For the all-actions view
      queryClient.invalidateQueries({ queryKey: ['grouped-actions', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['actionLogs', 'all'] });
      queryClient.resetQueries({ queryKey: ['actionLogs', 'all'] });
    }

    // Additionally invalidate any action-related queries
    queryClient.invalidateQueries({ queryKey: ['action-details'] });
    queryClient.invalidateQueries({ queryKey: ['related-actions'] });

    // Then perform a complete refetch
    refetch().then(() => {
      toast({
        description: 'Tasks refreshed with latest changes',
      });
    });
  };

  // Handle undo action
  const handleUndoAction = async (actionId: number) => {
    try {
      await undoActionMutation.mutateAsync(actionId);
      toast({
        description: 'Action undone successfully',
      });
      refreshData();
    } catch (error) {
      toast({
        variant: 'destructive',
        description: 'Failed to undo action',
      });
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    setPagination((prev) => ({
      ...prev,
      limit: prev.limit + 20,
    }));
  };
  

  // Render the left column with campaign task items
  const renderLeftColumn = () => {
    if (isLoading) {
      return (
        <div className='flex justify-center p-8'>
          <Spinner />
        </div>
      );
    }

    if (isError) {
      return (
        <div className='rounded-lg border border-red-100 bg-red-50 p-4 text-center text-red-600 my-6'>
          <p className='mb-2 font-medium'>Error loading task data</p>
          <p className='text-sm'>{error instanceof Error ? error.message : 'An unknown error occurred'}</p>
          <button
            className='mt-4 px-3 py-1 bg-white border border-red-200 rounded-md text-sm hover:bg-red-50'
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      );
    }

    // Empty state is now handled at the top level component
    if (campaignGroups.length === 0) {
      return (
        <div className='flex items-center justify-center h-full p-6'>
          <p className='text-muted-foreground text-sm'>No tasks available</p>
        </div>
      );
    }

    return (
      <div className='divide-y'>
        {campaignGroups.map((group) =>
          campaignId ? (
            // Use simplified item without campaign name when in campaign context
            <SimpleCampaignTaskItem
              key={group.id}
              campaignGroup={group as CampaignGroup}
              isSelected={selectedItem === group.id}
              onSelect={() => handleSelectItem(group.id)}
            />
          ) : (
            // Use regular item with campaign name in tasks page
            <SimpleTaskItem
              key={group.id}
              campaignGroup={group as CampaignGroup}
              isSelected={selectedItem === group.id}
              onSelect={() => handleSelectItem(group.id)}
            />
          ),
        )}
      </div>
    );
  };

  // Render the right column with appropriate component based on action type
  const renderRightColumn = () => {
    if (!selectedGroup || !enhancedAction) {
      return <EmptyStateView />;
    }

    try {
      // Use the action type to determine which component to render
      switch(enhancedAction.actionType) {
        case 'ADD_KEYWORDS':
        case 'ADD_EXACT_MATCH_KEYWORD':
        case 'PAUSE_KEYWORDS':
        case 'DELETE_KEYWORDS':
        case 'ADD_NEGATIVE_KEYWORD':
        case 'ADD_NEGATIVE_KEYWORD_FOR_AD':
        case 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN':
          return (
            <KeywordList
              action={enhancedAction as NormalizedAction}
              selectedKeywordId={selectedKeywordId}
              onSelectKeyword={handleSelectKeyword}
              onViewDetails={(keywordId) => handleViewDetails(enhancedAction.original, keywordId)}
            />
          );
          
        case 'SET_BUDGET':
          return (
            <BudgetDetail
              action={enhancedAction as NormalizedAction}
              onViewDetails={() => handleViewDetails(enhancedAction.original)}
              onUndoBudget={() => handleUndoAction(enhancedAction.id)}
            />
          );
          
        default:
          return (
            <GenericDetail
              action={enhancedAction as NormalizedAction}
              onViewDetails={() => handleViewDetails(enhancedAction.original)}
            />
          );
      }
    } catch (error) {
      // Error fallback for any rendering issues
      return (
        <div className="p-6 border border-red-200 rounded-md bg-white">
          <h3 className="text-red-600 font-medium mb-2">Error displaying recommendation</h3>
          <p className="text-sm text-gray-700 mb-4">
            The recommendation data couldn't be processed correctly.
          </p>
          <button 
            className="text-sm text-blue-600"
            onClick={() => {
              if (selectedGroup && selectedGroup.tasks.length > 0) {
                handleViewDetails(selectedGroup.tasks[0]);
              }
            }}
          >
            View Details
          </button>
        </div>
      );
    }
  };


  return (
    <div className='flex flex-col h-full'>
      <div className='flex-1 space-y-8 py-4 overflow-hidden'>
      {/* Task Detail Modal - using new consistent component */}
      {selectedAction && (
        <TaskDetailModal
          action={selectedAction}
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          selectedKeywordId={selectedKeywordId}
          onSelectKeyword={handleSelectKeyword}
          onActionUndone={refreshData}
        />
      )}

      {/* Header with tabs - Only show full header in the tasks page, not in campaign view */}
      {!campaignId && (
        <div className='flex items-center justify-between'>
          <PageHeader title={t('taskPage.title')} />
          <div className='flex items-center gap-3'>
            <Tabs defaultValue='active' value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value='active'>Active</TabsTrigger>
                <TabsTrigger value='reverted'>Reverted</TabsTrigger>
                <TabsTrigger value='all'>All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 h-full overflow-hidden' style={{ minHeight: campaignId ? '400px' : '60vh' }}>
        {/* Left column: Tasks list */}
        <div className='border rounded-md overflow-hidden h-full bg-white flex flex-col'>
          <div className='flex-1 overflow-y-auto'>{renderLeftColumn()}</div>
        </div>

        {/* Right column: Task details */}
        <div className='border rounded-md overflow-hidden h-full flex flex-col'>
          <div className='flex-1 overflow-y-auto'>{renderRightColumn()}</div>
        </div>
      </div>

      {/* Load more button - Only show in full task page, not in campaign view */}
      {!campaignId && data && data.total > 0 && data.items && data.items.length > 0 && data.total > data.items.length && (
        <div className='flex justify-center mt-6'>
          <Button variant='outline' onClick={handleLoadMore} className='w-full md:w-auto'>
            Load More ({data?.items?.length || 0} of {data?.total || 0})
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}